import React, { useState, useEffect, useMemo, useCallback } from 'react';
import Api from 'services/Api';

import {
  Card,
  Row,
  Col,
  Form,
  Button,
  Spinner,
  Alert,
  Badge,
  InputGroup,
  Modal,
} from 'react-bootstrap';

const EMPTY_ALARM_INPUTS = {
  silenciar: '',
  rearm_rst: '',
  rearm_buzzer: '',
};

const AddAlarm = ({
  units = {},
  companyId,
  serialNumber = [],
  permission,
  theme,
}) => {
  const isDark = theme === 'dark';
  const [alarms, setAlarms] = useState([]);
  const [loadingAlarms, setLoadingAlarms] = useState(true);
  const [selectedAlarmIds, setSelectedAlarmIds] = useState([]);
  const [selectedEquipments, setSelectedEquipments] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [notification, setNotification] = useState({ variant: '', message: '' });
  const [showUnlinkEquipmentsModal, setShowUnlinkEquipmentsModal] = useState(false);
  const [showRemoveModal, setShowRemoveModal] = useState(false);

  const resolvedSerialNumber =
    (!Array.isArray(serialNumber) ? serialNumber : '') ||
    units?.serialNumber ||
    units?.serialnumber ||
    (Array.isArray(units?.equipments) && units.equipments[0]) ||
    '';

  const targetCompanyId = companyId || units?._id || units?.id;
  const formatDateTime = (value) => {
    if (!value) return '--';
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return '--';
    return parsed.toLocaleString('pt-BR');
  };

  const getAlarmId = (alarm) => alarm._id || alarm.id || String(alarm.id || alarm.key || '');
  const getAlarmCompanyId = (alarm) => alarm?.companyId || alarm?.company_id || alarm?.company || '';
  const getAlarmDisplayText = (alarm) =>
    alarm?.serialNumber ||
    alarm?.name ||
    alarm?.label ||
    alarm?.description ||
    alarm?.title ||
    getAlarmId(alarm) ||
    'Alarme sem nome';

  const companyEquipments = useMemo(() => {
    const equipmentListFromProp = Array.isArray(serialNumber) ? serialNumber : [];
    const equipmentListFromUnit = Array.isArray(units?.equipments) ? units.equipments : [];
    const sourceList = equipmentListFromProp.length ? equipmentListFromProp : equipmentListFromUnit;

    return sourceList
      .map((item) => {
        if (typeof item === 'string') return item;
        return item?.serialnumber || item?.serialNumber || '';
      })
      .filter(Boolean)
      .filter((serial) => String(serial).toUpperCase().includes('SEMT'))
      .map(String)
      .filter((value, index, self) => self.indexOf(value) === index)
      .sort((a, b) => a.localeCompare(b, 'pt-BR', { numeric: true, sensitivity: 'base' }));
  }, [serialNumber, units?.equipments]);

  const availableAlarms = useMemo(() => {
    return alarms.filter((alarm) => {
      const alarmCompanyId = alarm.companyId || alarm.company_id || alarm.company;
      if (!alarmCompanyId) return true;
      return alarmCompanyId === targetCompanyId;
    })
      .sort((a, b) =>
        getAlarmDisplayText(a).localeCompare(getAlarmDisplayText(b), 'pt-BR', {
          numeric: true,
          sensitivity: 'base',
        })
      );
  }, [alarms, targetCompanyId]);

  const selectedAlarms = useMemo(() => alarms.filter((alarm) => {
    const alarmId = alarm._id || alarm.id || alarm.key;
    return alarmId && selectedAlarmIds.includes(String(alarmId));
  }), [alarms, selectedAlarmIds]);

  const selectedAlarm = selectedAlarms[0] || null;

  const alarmsForCompany = useMemo(() => alarms.filter((alarm) => {
    const alarmCompanyId = alarm.companyId || alarm.company_id || alarm.company;
    return targetCompanyId && alarmCompanyId === targetCompanyId;
  }), [alarms, targetCompanyId]);

  const alarmForSerial = useMemo(() => {
    return selectedAlarm ||
      alarmsForCompany[0] ||
      alarms.find(
        (alarm) =>
          alarm.serialNumber &&
          resolvedSerialNumber &&
          alarm.serialNumber === resolvedSerialNumber
      );
  }, [selectedAlarm, alarmsForCompany, alarms, resolvedSerialNumber]);

  const hasSelectedAlarmInCompany = useMemo(
    () => selectedAlarms.some((alarm) => getAlarmCompanyId(alarm) === targetCompanyId),
    [selectedAlarms, targetCompanyId]
  );

  const [alarmInputs, setAlarmInputs] = useState(EMPTY_ALARM_INPUTS);

  useEffect(() => {
    if (alarmForSerial) {
      setAlarmInputs({
        silenciar: alarmForSerial.silenciar ?? '',
        rearm_rst: alarmForSerial.rearm_rst ?? '',
        rearm_buzzer: alarmForSerial.rearm_buzzer ?? '',
      });
    } else {
      setAlarmInputs(EMPTY_ALARM_INPUTS);
    }
  }, [alarmForSerial]);

  const displaySerial = useMemo(() => {
    if (!selectedAlarmIds.length) {
      return '';
    }

    const serials = selectedAlarms
      .map((alarm) => alarm?.serialNumber)
      .filter(Boolean);

    if (serials.length === 1) {
      return serials[0];
    }

    if (serials.length > 1) {
      return `${serials.length} alarmes selecionados`;
    }

    return resolvedSerialNumber || '';
  }, [selectedAlarmIds, selectedAlarms, resolvedSerialNumber]);

  useEffect(() => {
    if (!hasSelectedAlarmInCompany) {
      setSelectedEquipments([]);
      return;
    }

    const alarmWithEquipments = selectedAlarms.find(
      (alarm) => getAlarmCompanyId(alarm) === targetCompanyId && Array.isArray(alarm?.equipments)
    );

    if (alarmWithEquipments) {
      setSelectedEquipments(alarmWithEquipments.equipments.map(String));
      return;
    }

    setSelectedEquipments([]);
  }, [hasSelectedAlarmInCompany, selectedAlarms, targetCompanyId]);

  const fetchAlarms = useCallback(async () => {
    let isMounted = true;
    setLoadingAlarms(true);
    try {
      const { data } = await Api.get('/alarm/v1/all');
      if (!isMounted) return;
      setAlarms(Array.isArray(data) ? data : []);
    } catch (error) {
      if (!isMounted) return;
      setNotification({
        variant: 'danger',
        message: 'Não foi possível carregar os alarmes disponíveis.',
      });
    } finally {
      if (isMounted) {
        setLoadingAlarms(false);
      }
    }

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    const load = async () => {
      const cleanup = await fetchAlarms();
      return cleanup;
    };

    let cleanupFn;
    load().then((cleanup) => { cleanupFn = cleanup; });

    return () => {
      if (typeof cleanupFn === 'function') cleanupFn();
    };
  }, [fetchAlarms]);

  useEffect(() => {
    if (!targetCompanyId || loadingAlarms || alarmsForCompany.length === 0) {
      return;
    }

    const linkedAlarmIds = alarmsForCompany
      .map((alarm) => getAlarmId(alarm))
      .filter(Boolean)
      .map((id) => String(id));

    setSelectedAlarmIds(linkedAlarmIds);
  }, [targetCompanyId, loadingAlarms, alarmsForCompany]);

  const toNumberOrUndefined = (value) => (value ? Number(value) : undefined);

  const buildAlarmPayload = (companyValue, alarm, equipmentsOverride) => {
    const payload = {
      companyId: companyValue,
      silenciar: toNumberOrUndefined(alarmInputs.silenciar),
      rearm_rst: toNumberOrUndefined(alarmInputs.rearm_rst),
      rearm_buzzer: toNumberOrUndefined(alarmInputs.rearm_buzzer),
    };

    if (getAlarmCompanyId(alarm) === targetCompanyId || companyValue === targetCompanyId) {
      payload.equipments = Array.isArray(equipmentsOverride) ? equipmentsOverride : selectedEquipments;
    }

    return payload;
  };

  const updateAlarmBindings = async (alarmsToUpdate, companyValue, equipmentsOverride) => {
    await Promise.all(
      alarmsToUpdate
        .filter((alarm) => alarm?.serialNumber)
        .map((alarm) =>
          Api.put(
            `/alarm/v1/${encodeURIComponent(alarm.serialNumber)}`,
            buildAlarmPayload(companyValue, alarm, equipmentsOverride)
          )
        )
    );
  };

  const handleSave = async () => {
    if (!targetCompanyId || !selectedAlarms.length) {
      setNotification({
        variant: 'danger',
        message: 'Selecione ao menos um alarme e garanta que a empresa esteja definida.',
      });
      return;
    }

    setIsSaving(true);
    setNotification({ variant: '', message: '' });

    try {
      await updateAlarmBindings(selectedAlarms, targetCompanyId);
      setNotification({
        variant: 'success',
        message: `${selectedAlarms.length} alarme(s) vinculado(s) e configurado(s) com sucesso.`,
      });
      await fetchAlarms();
    } catch (error) {
      const serverMessage =
        error?.response?.data?.message ||
        'Falha ao salvar o alarme selecionado.';
      setNotification({
        variant: 'danger',
        message: serverMessage,
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleOpenUnlinkEquipmentsModal = () => {
    const alarmsInCompany = selectedAlarms.filter(
      (alarm) => alarm?.serialNumber && getAlarmCompanyId(alarm) === targetCompanyId
    );

    if (!alarmsInCompany.length) {
      setNotification({
        variant: 'warning',
        message: 'Selecione ao menos um alarme vinculado a esta empresa para retirar os equipamentos.',
      });
      return;
    }
    setShowUnlinkEquipmentsModal(true);
  };

  const handleConfirmUnlinkEquipments = async () => {
    const alarmsInCompany = selectedAlarms.filter(
      (alarm) => alarm?.serialNumber && getAlarmCompanyId(alarm) === targetCompanyId
    );
    setShowUnlinkEquipmentsModal(false);

    setIsSaving(true);
    setNotification({ variant: '', message: '' });

    try {
      await updateAlarmBindings(alarmsInCompany, targetCompanyId, []);
      setSelectedEquipments([]);
      setNotification({
        variant: 'success',
        message: `Equipamentos desvinculados de ${alarmsInCompany.length} alarme(s).`,
      });
      await fetchAlarms();
    } catch (error) {
      const serverMessage =
        error?.response?.data?.message ||
        'Falha ao retirar equipamentos vinculados ao alarme.';
      setNotification({
        variant: 'danger',
        message: serverMessage,
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancelUnlinkEquipments = () => {
    setShowUnlinkEquipmentsModal(false);
  };

  const handleRemoveClick = () => {
    if (!selectedAlarms.length) {
      setNotification({
        variant: 'warning',
        message: 'Selecione ao menos um alarme para remover.',
      });
      return;
    }
    setShowRemoveModal(true);
  };

  const handleConfirmRemove = async () => {
    const alarmsWithSerial = selectedAlarms.filter((alarm) => alarm?.serialNumber);
    if (!alarmsWithSerial.length) {
      setNotification({
        variant: 'danger',
        message: 'Serial do(s) alarme(s) não encontrado.',
      });
      setShowRemoveModal(false);
      return;
    }

    setIsSaving(true);
    setNotification({ variant: '', message: '' });
    setShowRemoveModal(false);

    try {
      await updateAlarmBindings(alarmsWithSerial, '');
      setNotification({
        variant: 'success',
        message: `${alarmsWithSerial.length} alarme(s) desvinculado(s) com sucesso.`,
      });
      setSelectedAlarmIds([]);
      setAlarmInputs(EMPTY_ALARM_INPUTS);
      await fetchAlarms();
    } catch (error) {
      const serverMessage =
        error?.response?.data?.message ||
        'Falha ao remover o alarme.';
      setNotification({
        variant: 'danger',
        message: serverMessage,
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancelRemove = () => {
    setShowRemoveModal(false);
  };

  const hasVisibleContent =
    loadingAlarms ||
    availableAlarms.length > 0 ||
    alarmsForCompany.length > 0 ||
    selectedAlarmIds.length > 0;

  if (!hasVisibleContent) {
    return null;
  }

  return (
    <Card className={`mb-3 mt-3`}>
      <Card.Header>
        <Row className='align-items-center'>
          <Col>
            <h5 className={`mt-2 mb-2 ${theme === 'dark' ? 'text-light' : 'text-dark'}`}>
              Alarmes Monitoramento
            </h5>
          </Col>
          <Col xs='auto'>
            <small className={isDark ? 'text-light opacity-75' : 'text-muted'}>
              Serial:{' '}
              {displaySerial || (
                <span className='text-danger'>não informado</span>
              )}
            </small>
          </Col>
        </Row>
      </Card.Header>

      <Card.Body>
        {notification.message && (
          <Alert variant={notification.variant || 'info'} className='mb-3'>
            {notification.message}
          </Alert>
        )}

        <Form>
          <div className='d-flex align-items-start gap-2'>
            <div style={{ flex: 1 }}>
              <Form.Group controlId='alarmSelect'>
                <Form.Label className={`${theme === 'dark' ? 'text-light' : 'text-dark'}`}>Selecione um ou mais alarmes</Form.Label>
                <Form.Select
                  multiple
                  value={selectedAlarmIds}
                  style={{ minHeight: 180 }}
                  className={isDark ? 'bg-dark text-light border-secondary' : ''}
                  disabled={loadingAlarms || !availableAlarms.length}
                  onChange={(event) => {
                    const values = Array.from(event.target.selectedOptions, (option) => option.value);
                    setSelectedAlarmIds(values);
                  }}
                >
                  {availableAlarms.map((alarm) => {
                    const alarmId = getAlarmId(alarm);
                    const label = getAlarmDisplayText(alarm);
                    const alarmCompanyId = getAlarmCompanyId(alarm);
                    const sameCompany = targetCompanyId && alarmCompanyId === targetCompanyId;
                    const optionLabel = sameCompany
                      ? `${label} - vinculado a esta empresa`
                      : label;
                    return (
                      <option key={alarmId || `${label}-${alarm.serialNumber}`} value={String(alarmId)}>
                        {optionLabel}
                      </option>
                    );
                  })}
                </Form.Select>
                <Form.Text className={isDark ? 'text-light opacity-75' : ''}>
                  Segure Ctrl (ou Cmd no Mac) para selecionar múltiplos alarmes.
                </Form.Text>
                {loadingAlarms && (
                  <Form.Text className={isDark ? 'text-light opacity-75' : ''}>Carregando alarmes do monitoramento...</Form.Text>
                )}
                {!loadingAlarms && !availableAlarms.length && (
                  <Form.Text className={isDark ? 'text-light opacity-75' : ''}>Sem alarmes disponíveis para esta cooperativa.</Form.Text>
                )}
                {!targetCompanyId && (
                  <Form.Text className='text-danger'>
                    Empresa não identificada para o envio.
                  </Form.Text>
                )}
              </Form.Group>
            </div>

            {hasSelectedAlarmInCompany && (
              <div style={{ flex: 1 }}>
                <Form.Group controlId='equipmentSelect'>
                  <Form.Label className={`${theme === 'dark' ? 'text-light' : 'text-dark'}`}>
                    Escolha os equipamentos para o(s) alarme(s)
                  </Form.Label>
                  <Form.Select
                    multiple
                    value={selectedEquipments}
                    style={{ minHeight: 180 }}
                    className={isDark ? 'bg-dark text-light border-secondary' : ''}
                    disabled={!companyEquipments.length}
                    onChange={(event) => {
                      const values = Array.from(event.target.selectedOptions, (option) => option.value);
                      setSelectedEquipments(values);
                    }}
                  >
                    {companyEquipments.map((equipmentSerial) => (
                      <option key={equipmentSerial} value={equipmentSerial}>
                        {equipmentSerial}
                      </option>
                    ))}
                  </Form.Select>
                  <Form.Text className={isDark ? 'text-light opacity-75' : ''}>
                    Esses equipamentos serão enviados no campo `equipments` do alarme.
                  </Form.Text>
                </Form.Group>
              </div>
            )}

            <div className='d-flex flex-column gap-2' style={{ paddingTop: '32px', flexShrink: 0, minWidth: 200 }}>
              <Button
                variant='success'
                disabled={
                  !permission ||
                  isSaving ||
                  !selectedAlarmIds.length ||
                  !targetCompanyId
                }
                onClick={handleSave}
              >
                {isSaving ? (
                  <>
                    <Spinner
                      animation='border'
                      size='sm'
                      role='status'
                      aria-hidden='true'
                      className='me-2'
                    />
                    Salvando
                  </>
                ) : (
                  'Salvar'
                )}
              </Button>

              {hasSelectedAlarmInCompany && (
                <Button
                  variant='outline-warning'
                  disabled={!permission || isSaving}
                  onClick={handleOpenUnlinkEquipmentsModal}
                >
                  Retirar Equipamentos
                </Button>
              )}

              {alarmForSerial && (
                <Button
                  variant='danger'
                  disabled={!permission || isSaving}
                  onClick={handleRemoveClick}
                >
                  Remover Alarme
                </Button>
              )}
            </div>
          </div>
        </Form>

        {alarmForSerial && (
          <Card className={`mt-3 border-0 shadow-sm`}>
            <Card.Body className={`p-0`}>
              <div className='d-flex align-items-start justify-content-between mb-3'>
                <div>
                  <span className={`mt-2 mb-2 ${theme === 'dark' ? 'text-light' : 'text-dark'}`}>Configurações do alarme</span>
                  <h5 className={`mt-2 mb-2 ${theme === 'dark' ? 'text-light' : 'text-dark'}`}>{alarmForSerial.serialNumber || 'Serial indefinido'}</h5>
                </div>
              </div>

              <Row className='g-3'>
                <Col md={4}>
                  <div
                    className={`p-3 rounded-3 border h-100 ${isDark ? 'bg-dark text-light border-secondary' : 'bg-white'}`}
                    style={{ minHeight: 140 }}
                  >
                    <div className={`text-uppercase small mb-2 ${isDark ? 'text-light opacity-75' : 'text-secondary'}`}>Última atualização</div>
                    <div className='fs-6 fw-semibold'>{formatDateTime(alarmForSerial.lastUpdate)}</div>
                    <div className={`small mt-3 ${isDark ? 'text-light opacity-75' : 'text-muted'}`}>Última sincronização visível</div>
                  </div>
                </Col>
                <Col md={4}>
                  <div className={`p-3 rounded-3 border h-100 ${isDark ? 'bg-dark text-light border-secondary' : 'bg-white'}`} style={{ minHeight: 140 }}>
                    <div className={`text-uppercase small mb-2 ${isDark ? 'text-light opacity-75' : 'text-secondary'}`}>Criado em</div>
                    <div className='fs-6 fw-semibold'>{formatDateTime(alarmForSerial.createdAt)}</div>
                    <div className={`text-uppercase small mt-3 mb-1 ${isDark ? 'text-light opacity-75' : 'text-secondary'}`}>Atualizado em</div>
                    <div className='fs-6 fw-semibold'>{formatDateTime(alarmForSerial.updatedAt)}</div>
                  </div>
                </Col>
                <Col md={4}>
                  <div className={`p-3 rounded-3 border h-100 ${isDark ? 'bg-secondary text-light border-secondary' : 'bg-light'}`}>
                    <div className={`text-uppercase small mb-2 ${isDark ? 'text-light opacity-75' : 'text-secondary'}`}>Status geral</div>
                    <div className={`fs-6 fw-semibold ${isDark ? 'text-light' : 'text-dark'}`}>
                      {alarmForSerial.silenciar ? `Silenciar ${alarmForSerial.silenciar}s` : 'Silêncio padrão'}
                    </div>
                    <div className={`small mt-2 ${isDark ? 'text-light opacity-75' : 'text-muted'}`}>Rearms programados</div>
                    <div className='d-flex gap-2 flex-wrap mt-1'>
                      <Badge bg={isDark ? 'dark' : 'light'} text={isDark ? 'light' : 'dark'} className='border border-secondary'>
                        Reset {alarmForSerial.rearm_rst ?? '—'}
                      </Badge>
                      <Badge bg={isDark ? 'dark' : 'light'} text={isDark ? 'light' : 'dark'} className='border border-secondary'>
                        Buzzer {alarmForSerial.rearm_buzzer ?? '—'}
                      </Badge>
                    </div>
                  </div>
                </Col>
              </Row>

              <hr className={`my-3 ${isDark ? 'border-secondary' : ''}`} />
              <Row className='g-3'>
                
                <Col md={4}>
                  <Form.Group controlId='alarmSilenciar'>
                    <Form.Label className={`small ${isDark ? 'text-light opacity-75' : 'text-muted'}`}>Tempo de sirene ativa</Form.Label>
                    <InputGroup>
                      <InputGroup.Text className={isDark ? 'bg-secondary text-light border-secondary' : ''}>⏱️</InputGroup.Text>
                      <Form.Control
                        type='number'
                        className={isDark ? 'bg-dark text-light border-secondary' : ''}
                        value={alarmInputs.silenciar}
                        onChange={(event) =>
                          setAlarmInputs(prev => ({
                            ...prev,
                            silenciar: event.target.value,
                          }))
                        }
                      />
                    </InputGroup>
                  </Form.Group>
                </Col>

                <Col md={4}>
                  <Form.Group controlId='alarmRearmBuzzer'>
                    <Form.Label className={`small ${isDark ? 'text-light opacity-75' : 'text-muted'}`}>Intervalo de rearme da sirene</Form.Label>
                    <InputGroup>
                      <InputGroup.Text className={isDark ? 'bg-secondary text-light border-secondary' : ''}>🔔</InputGroup.Text>
                      <Form.Control
                        type='number'
                        className={isDark ? 'bg-dark text-light border-secondary' : ''}
                        value={alarmInputs.rearm_buzzer}
                        onChange={(event) =>
                          setAlarmInputs(prev => ({
                            ...prev,
                            rearm_buzzer: event.target.value,
                          }))
                        }
                      />
                    </InputGroup>
                  </Form.Group>
                </Col>

                <Col md={4}>
                  <Form.Group controlId='alarmRearmRst'>
                    <Form.Label className={`small ${isDark ? 'text-light opacity-75' : 'text-muted'}`}>Intervalo de rearme após reset</Form.Label>
                    <InputGroup>
                      <InputGroup.Text className={isDark ? 'bg-secondary text-light border-secondary' : ''}>🔁</InputGroup.Text>
                      <Form.Control
                        type='number'
                        className={isDark ? 'bg-dark text-light border-secondary' : ''}
                        value={alarmInputs.rearm_rst}
                        onChange={(event) =>
                          setAlarmInputs(prev => ({
                            ...prev,
                            rearm_rst: event.target.value,
                          }))
                        }
                      />
                    </InputGroup>
                  </Form.Group>
                </Col>

              </Row>
            </Card.Body>
          </Card>
        )}
      </Card.Body>

      <Modal
        show={showUnlinkEquipmentsModal}
        onHide={handleCancelUnlinkEquipments}
        centered
        contentClassName={isDark ? 'bg-dark text-light border-secondary' : 'bg-white text-dark'}
      >
        <Modal.Header closeButton closeVariant={isDark ? 'white' : undefined} className={isDark ? 'bg-dark text-light border-secondary' : ''}>
          <Modal.Title>Confirmar Retirada de Equipamentos</Modal.Title>
        </Modal.Header>
        <Modal.Body className={isDark ? 'bg-dark text-light' : ''}>
          <p>
            Tem certeza que deseja retirar os equipamentos vinculados de <strong>{selectedAlarms.length}</strong> alarme(s)?
          </p>
        </Modal.Body>
        <Modal.Footer className={isDark ? 'bg-dark text-light border-secondary' : ''}>
          <Button variant="secondary" onClick={handleCancelUnlinkEquipments}>
            Cancelar
          </Button>
          <Button variant="warning" onClick={handleConfirmUnlinkEquipments} disabled={isSaving}>
            {isSaving ? (
              <>
                <Spinner
                  animation='border'
                  size='sm'
                  role='status'
                  aria-hidden='true'
                  className='me-2'
                />
                Processando...
              </>
            ) : (
              'Confirmar Retirada'
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal de confirmação de remoção */}
      <Modal
        show={showRemoveModal}
        onHide={handleCancelRemove}
        centered
        contentClassName={isDark ? 'bg-dark text-light border-secondary' : 'bg-white text-dark'}
      >
        <Modal.Header closeButton closeVariant={isDark ? 'white' : undefined} className={isDark ? 'bg-dark text-light border-secondary' : ''}>
          <Modal.Title>Confirmar Remoção</Modal.Title>
        </Modal.Header>
        <Modal.Body className={isDark ? 'bg-dark text-light' : ''}>
          <p>
            Tem certeza que deseja desvincular <strong>{selectedAlarms.length}</strong> alarme(s) desta cooperativa?
          </p>
          <p className={`small mb-0 ${isDark ? 'text-light opacity-75' : 'text-muted'}`}>
            O alarme não será deletado, apenas desvinculado e ficará disponível para ser configurado novamente.
          </p>
        </Modal.Body>
        <Modal.Footer className={isDark ? 'bg-dark text-light border-secondary' : ''}>
          <Button variant="secondary" onClick={handleCancelRemove}>
            Cancelar
          </Button>
          <Button
            variant="danger"
            onClick={handleConfirmRemove}
            disabled={isSaving}
          >
            {isSaving ? (
              <>
                <Spinner
                  animation='border'
                  size='sm'
                  role='status'
                  aria-hidden='true'
                  className='me-2'
                />
                Removendo...
              </>
            ) : (
              'Confirmar Remoção'
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </Card>
  );
};

export default AddAlarm;
